import { useCallback, useContext, useMemo, useState } from "react";
import AttachmentContext from "../context/attachmentsContext";
import { useAttachmentSelection } from "./useAttachmentSelection";
import {
  flatMapFilesAndVersions,
  compileAttachmentsForPresentation,
} from "../views/Activities/NewActivity/newActivity.utils";

/**
 * Encapsulates the shared "raw project attachments -> selectable list" pipeline
 * used identically by NewActivity (research/modal flow) and ContentItem (the
 * visible non-research list):
 *   raw files -> flatMapFilesAndVersions -> compileAttachmentsForPresentation
 *   -> compiledFiles state -> applySelection -> stateFiles
 *
 * It also owns the attachment selection state (via useAttachmentSelection), so
 * each consumer gets its own isolated selection.
 *
 * @returns {{
 *   stateFiles: Array,
 *   compile: (rawFiles: Array, chapterEpisodeType: string, type: string) => void,
 *   clearCompiled: () => void,
 *   activeKeys: Set<string>,
 *   toggleActiveCheckBox: (fileOrParent: object) => void,
 *   toggleActiveVersionCheckBox: (version: object) => void,
 *   clearActive: () => void,
 *   resetSelection: (keys?: Array<string|null>) => void,
 *   applySelection: (compiledFiles: Array) => Array,
 * }}
 */
export const useCompiledAttachments = ({ groupSupplementaryMaterial } = {}) => {
  const attachmentContext = useContext(AttachmentContext);
  const [compiledFiles, setCompiledFiles] = useState([]);
  const selection = useAttachmentSelection();
  const { applySelection } = selection;

  const stateFiles = useMemo(
    () => applySelection(compiledFiles),
    [compiledFiles, applySelection]
  );

  const compile = useCallback(
    (rawFiles, chapterEpisodeType, type) => {
      if (!rawFiles?.length) {
        setCompiledFiles([]);
        return;
      }
      setCompiledFiles(
        compileAttachmentsForPresentation({
          attachmentContext,
          data: flatMapFilesAndVersions(rawFiles),
          chapterEpisodeType,
          type,
          groupSupplementaryMaterial,
        })
      );
    },
    [attachmentContext, groupSupplementaryMaterial]
  );

  const clearCompiled = useCallback(() => setCompiledFiles([]), []);

  return useMemo(
    () => ({ ...selection, stateFiles, compile, clearCompiled }),
    [selection, stateFiles, compile, clearCompiled]
  );
};
